<script lang="ts" setup>
import type { RouteLocationRaw } from "vue-router";
import type { Breadcrumb } from "~/core/models/Breadcrumb.interface";
import ILxcArrowLeft from "~icons/lxc/arrow-left";

const props = defineProps<{
  /**
   * Breadcrumbs to display.
   */
  breadcrumbs?: Breadcrumb[];

  /**
   * True to prefix the breadcrumbs with the breadcrumb defined in the route meta, false otherwise.
   * Default: false
   */
  useRouteMeta?: boolean;

  /**
   * True to use breadcrumb href for redirect, false otherwise.
   * Default: false
   */
  useHref?: boolean;

  /**
   * The route to go back.
   * The back button is displayed if this property is defined.
   */
  backRoute?: RouteLocationRaw;

  /**
   * The default route to go back if the back route is not provided and the previous page does not exist.
   * The back button is displayed if this property is defined.
   */
  backDefaultRoute?: RouteLocationRaw;

  /**
   * List of the routes to exclude for the back button.
   * If the back route is among the excluded ones, the default back route is used.
   */
  backExcludeRoutes?: RouteLocationRaw[];
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const breadcrumb = route.meta.breadcrumb as Breadcrumb;

const computedBreadcrumbs: ComputedRef<Breadcrumb[]> = computed(() =>
  (props.useRouteMeta && breadcrumb && breadcrumb.title
    ? ([{ ...breadcrumb, title: t(breadcrumb.title) }] as Breadcrumb[])
    : []
  )
    .concat(props.breadcrumbs ? props.breadcrumbs : [])
    .filter((breadcrumb) => breadcrumb.title),
);

async function goBack() {
  if (props.backRoute && !props.backExcludeRoutes?.includes(props.backRoute)) {
    await router.push(props.backRoute);
  } else {
    const previousPage = localStorage.getItem("previousPage");
    if (previousPage && !props.backExcludeRoutes?.includes(previousPage)) {
      await router.push(previousPage);
    } else if (props.backDefaultRoute) {
      await router.push(props.backDefaultRoute);
    } else {
      router.back();
    }
  }
}

onMounted(() => {
  if (
    !localStorage.getItem("previousPage") &&
    router.options.history.state.back
  ) {
    localStorage.setItem(
      "previousPage",
      router.options.history.state.back.toString(),
    );
  }
});

onUnmounted(() => {
  localStorage.removeItem("previousPage");
});

defineExpose({
  goBack,
});
</script>

<template>
  <div class="h-11 flex justify-start items-center">
    <lxc-button
      v-if="backRoute || backDefaultRoute"
      type="tertiary"
      :title="t('button.back')"
      class="h-11 w-11 mr-4"
      @click="goBack"
    >
      <i-lxc-arrow-left class="w-5 h-5 text-gray-700" />
    </lxc-button>

    <slot name="default">
      <template
        v-for="(computedBreadcrumb, index) in computedBreadcrumbs"
        :key="computedBreadcrumb.title"
      >
        <h2 class="text-3xl font-semibold text-gray-900 my-4">
          <router-link
            v-if="useHref && computedBreadcrumb.href"
            :to="computedBreadcrumb.href"
            class="!text-gray-900 !no-underline hover:opacity-65"
            >{{ computedBreadcrumb.title }}</router-link
          >
          <template v-else>{{ computedBreadcrumb.title }}</template>
        </h2>

        <i-lxc-chevron-right
          v-if="index < computedBreadcrumbs.length - 1"
          class="h-6 w-6 mx-4"
        />
      </template>
    </slot>
  </div>
</template>
